import {
  AddonGroupSubItemType,
  AddonGroupType,
  CategoryType,
  ItemDetailsType,
  ItemType,
  SubItemType,
} from "../types";

type MenuEntityWithUuid = {
  uuid?: string;
  id?: number | string;
};

/** Canonical menu entity key: uuid, then numeric/string id fallback. No externalId. */
export function resolveMenuEntityUuid(
  entity: MenuEntityWithUuid
): string | undefined {
  if (entity.uuid) {
    return entity.uuid;
  }

  if (entity.id != null) {
    return String(entity.id);
  }

  return undefined;
}

export function menuEntityMatchesId(
  entity: MenuEntityWithUuid,
  id: string
): boolean {
  return entity.uuid === id || (entity.id != null && String(entity.id) === id);
}

/**
 * Strict menu-entity uuid for the loyalty wire: the shared uuid only, never the
 * numeric id fallback. Loyalty order payloads are tagged idType:'uuid', so a
 * numeric id shipped here silently misses loyalty's uuid lookup (no offer
 * applied / mispricing) with no error surfaced. Fail fast instead — mirrors the
 * custom-mobile-apps path. Use only when building the loyalty payload; keep
 * resolveMenuEntityUuid (with its id fallback) for local cart matching.
 */
export function requireMenuEntityUuid(
  entity: MenuEntityWithUuid,
  label: string
): string {
  if (entity.uuid) {
    return entity.uuid;
  }
  throw new Error(
    `${label}: menu entity is missing its uuid (id=${
      entity.id ?? "unknown"
    }); refusing to send a numeric id under idType='uuid'.`
  );
}

/** Strict addon sub-item uuid (nested subItem preferred), never a numeric id. */
export function requireAddonSubItemUuid(
  addonGroupSubItem: AddonGroupSubItemType
): string {
  const uuid = addonGroupSubItem.subItem?.uuid ?? addonGroupSubItem.uuid;
  if (uuid) {
    return uuid;
  }
  throw new Error(
    "requireAddonSubItemUuid: addon sub-item is missing its uuid; refusing to send a numeric id under idType='uuid'."
  );
}

export function resolveSubItemUuid(subItem: SubItemType): string | undefined {
  return resolveMenuEntityUuid(subItem);
}

export function resolveItemUuid(
  item: Pick<ItemType | ItemDetailsType, "uuid" | "id">
): string | undefined {
  return resolveMenuEntityUuid(item);
}

export function resolveCategoryUuid(
  category: CategoryType
): string | undefined {
  return resolveMenuEntityUuid(category);
}

export function resolveAddonGroupUuid(
  group: AddonGroupType
): string | undefined {
  return resolveMenuEntityUuid(group);
}

export function resolveAddonSubItemUuid(
  addonGroupSubItem: AddonGroupSubItemType
): string | undefined {
  const subItem = addonGroupSubItem.subItem;

  if (subItem?.uuid) {
    return subItem.uuid;
  }

  if (addonGroupSubItem.uuid) {
    return addonGroupSubItem.uuid;
  }

  if (subItem?.id != null) {
    return String(subItem.id);
  }

  if (addonGroupSubItem.id != null) {
    return String(addonGroupSubItem.id);
  }

  return undefined;
}
